import { motion } from "framer-motion";
import {
  Building2, GraduationCap, Stethoscope, Briefcase,
  Cpu, HardHat, UtensilsCrossed, Factory, Store,
  TrendingUp, Sparkles,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const industries = [
  {
    name: "Real Estate", icon: Building2, img: "https://images.pexels.com/photos/18893526/pexels-photo-18893526.jpeg",
    color: "from-neon-orange to-neon-magenta",
    challenges: ["Long sales cycles", "Untracked leads", "Manual follow-ups"],
    solutions: ["AI lead routing", "CRM automation", "Conversion-tuned funnels"],
    opportunity: "4–10x qualified leads, 60% less manual ops.",
  },
  {
    name: "Healthcare", icon: Stethoscope, img: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1200&q=80",
    color: "from-neon-cyan to-neon-blue",
    challenges: ["Patient acquisition costs", "Reminder no-shows", "Fragmented data"],
    solutions: ["Lifecycle automation", "AI scheduling agents", "Unified patient CRM"],
    opportunity: "30–50% lift in bookings, 70% less ops time.",
  },
  {
    name: "Education", icon: GraduationCap, img: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1200&q=80",
    color: "from-neon-blue to-neon-purple",
    challenges: ["High CPL", "Low show-up rates", "Slow nurture"],
    solutions: ["AI nurture journeys", "Conversion bots", "Programmatic SEO"],
    opportunity: "3x enrolments at ~85% lower cost / lead.",
  },
  {
    name: "Professional Services", icon: Briefcase, img: "https://images.pexels.com/photos/6285113/pexels-photo-6285113.jpeg",
    color: "from-neon-purple to-neon-magenta",
    challenges: ["Word-of-mouth ceiling", "No predictable pipeline", "Manual proposals"],
    solutions: ["Brand & authority engine", "Outbound + inbound stack", "Proposal automations"],
    opportunity: "Predictable pipeline within 90 days.",
  },
  {
    name: "Technology / SaaS", icon: Cpu, img: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=1200&q=80",
    color: "from-neon-magenta to-neon-purple",
    challenges: ["High churn", "Activation drop-offs", "Attribution chaos"],
    solutions: ["Onboarding automation", "Lifecycle copilots", "Revenue attribution"],
    opportunity: "MRR up, churn down, clear unit economics.",
  },
  {
    name: "Construction", icon: HardHat, img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
    color: "from-neon-orange to-neon-cyan",
    challenges: ["Slow project pipeline", "Disorganized intake", "Bid leakage"],
    solutions: ["Lead capture + qualification", "Project CRM", "Quote automation"],
    opportunity: "Win-rate up, intake under control.",
  },
  {
    name: "Hospitality", icon: UtensilsCrossed, img: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1200&q=80",
    color: "from-neon-magenta to-neon-orange",
    challenges: ["Seasonality", "Low repeat rate", "Off-platform leakage"],
    solutions: ["Direct-booking funnels", "Loyalty automations", "Brand systems"],
    opportunity: "Lower OTA dependence, higher direct revenue.",
  },
  {
    name: "Manufacturing", icon: Factory, img: "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=1200&q=80",
    color: "from-neon-blue to-neon-cyan",
    challenges: ["B2B distribution gaps", "Manual RFQs", "Slow lead handoff"],
    solutions: ["B2B demand engine", "RFQ automations", "Sales CRM"],
    opportunity: "Pipeline visibility + faster cycle time.",
  },
  {
    name: "Retail / E-commerce", icon: Store, img: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&q=80",
    color: "from-neon-purple to-neon-cyan",
    challenges: ["Rising CAC", "Cart abandonment", "Inventory blind spots"],
    solutions: ["Performance + CRO", "Lifecycle marketing", "AI recommendations"],
    opportunity: "Profitable acquisition, repeat revenue lift.",
  },
];

export default function IndustriesPage() {
  return (
    <div data-testid="industries-root">
      <PageHero
        eyebrow="Industries"
        title={<>Built for businesses ready to <span className="text-gradient-warm">scale.</span></>}
        subtitle="The growth playbook changes per industry. We've shipped systems across nine verticals — here's where we go deep."
        accent="warm"
      />

      <section className="section pt-0">
        <div className="container-x space-y-6">
          {industries.map((ind, i) => {
            const Icon = ind.icon;
            const reverse = i % 2 === 1;
            return (
              <motion.article
                key={ind.name}
                data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.55 }}
                className="relative glass rounded-3xl overflow-hidden"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 ${reverse ? "lg:[direction:rtl]" : ""}`}>
                  <div className="relative h-64 lg:h-auto lg:min-h-[360px] [direction:ltr]">
                    <img src={ind.img} alt={ind.name} className="absolute inset-0 w-full h-full object-cover opacity-75" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${ind.color} opacity-25 mix-blend-screen`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg-1 to-transparent" />
                    <div className="absolute top-5 left-5 inline-flex items-center gap-2 glass-strong rounded-full pl-2 pr-3 py-1.5">
                      <span className={`w-6 h-6 rounded-md bg-gradient-to-br ${ind.color} flex items-center justify-center`}>
                        <Icon size={13} />
                      </span>
                      <span className="text-[12px] font-mono uppercase tracking-[0.18em]">{ind.name}</span>
                    </div>
                  </div>

                  <div className="p-7 sm:p-10 [direction:ltr]">
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{ind.name}</h2>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Block title="Challenges" color="text-rose-300" items={ind.challenges} />
                      <Block title="Our Solutions" color="text-emerald-300" items={ind.solutions} />
                    </div>
                    <div className="mt-5 flex items-center gap-2 text-sm font-semibold">
                      <TrendingUp size={16} className="text-emerald-400" />
                      <span className="text-white/85">{ind.opportunity}</span>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      <CTABand
        headline={<>Industry-specific growth, <span className="text-gradient-neon">engineered.</span></>}
        sub="Tell us your industry. We&apos;ll send a tailored growth blueprint with benchmarks and next steps."
        primary={{ label: "Get Industry Blueprint", to: "/contact" }}
        secondary={{ label: "See Case Studies", to: "/case-studies" }}
      />
    </div>
  );
}

function Block({ title, items, color }) {
  return (
    <div>
      <div className={`font-mono text-[10px] tracking-[0.18em] uppercase ${color} mb-2`}>{title}</div>
      <ul className="space-y-1.5 text-sm text-white/75">
        {items.map((x) => (
          <li key={x} className="flex items-start gap-2">
            <Sparkles size={11} className="text-white/40 mt-1" />
            <span>{x}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
